import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
  TablePagination,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Snackbar,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  History as HistoryIcon,
  DateRange as DateIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Description as TemplateIcon,
  Close as CloseIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import {
  getAllBatches,
  getBatchDetails,
  getBatchHistory,
  updateBatchItem,
  updateBatchBulk,
  deleteBatchItem,
  deleteBatchBulk,
  exportBatchesCSV,
  importBatchesCSV,
  downloadBatchCSVTemplate
} from '../../services/inventoryService';
import { PageHeader } from '../ds';

// Helper functions
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const BatchManagement = () => {
  const navigate = useNavigate();
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const fileInputRef = useRef(null);

  // Handle brand change
  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    if (brandId) {
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        selectBrand(brand);
      }
    }
  };

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedBatch, setExpandedBatch] = useState(null);

  // Batch detail view
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchHistory, setBatchHistory] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState('single'); // 'single' or 'bulk'
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({
    batch_number: '',
    sku: '',
    expiry_date: '',
    manufacturing_date: '',
    receipt_date: '',
    bin_location: '',
    gtin: '',
    asin: '',
    notes: '',
    unit_cost: '',
    received_quantity: ''
  });
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('single'); // 'single' or 'bulk'
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Load batches
  const loadBatches = async () => {
    if (!selectedBrand?.id) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getAllBatches({
        brand_id: selectedBrand.id,
        limit: 500
      });
      setBatches(result.batches || []);
    } catch (err) {
      console.error('Error loading batches:', err);
      setError(err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, [selectedBrand?.id]);

  // Load batch details
  // Only the newest batch load may apply its answer: opening batch B while A is still loading must not show A's
  // details and history in B's panel (DT-1139 sweep, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  const loadBatchDetails = async (batch) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    setSelectedBatch(batch);
    setDetailLoading(true);
    setBatchDetails(null);
    setBatchHistory(null);

    try {
      const [details, history] = await Promise.all([
        getBatchDetails(batch.batch_number, batch.sku),
        getBatchHistory(batch.batch_number, batch.sku)
      ]);
      if (stale()) return;
      setBatchDetails(details);
      setBatchHistory(history);
    } catch (err) {
      if (stale()) return;
      console.error('Error loading batch details:', err);
      setError(err.message || 'Failed to load batch details');
    } finally {
      if (!stale()) setDetailLoading(false);
    }
  };

  // Filter batches
  const filteredBatches = useMemo(() => {
    if (!searchTerm) return batches;
    const search = searchTerm.toLowerCase();
    return batches.filter(batch =>
      batch.batch_number?.toLowerCase().includes(search) ||
      batch.sku?.toLowerCase().includes(search) ||
      batch.product_name?.toLowerCase().includes(search) ||
      batch.gtin?.includes(search) ||
      batch.asin?.toLowerCase().includes(search)
    );
  }, [batches, searchTerm]);

  // Paginated batches
  const paginatedBatches = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredBatches.slice(start, start + rowsPerPage);
  }, [filteredBatches, page, rowsPerPage]);

  // Open edit dialog
  const openEditDialog = (item, mode = 'single', batch = null) => {
    setEditMode(mode);
    setEditItem(item);
    if (mode === 'bulk' && batch) {
      setEditForm({
        batch_number: batch.batch_number || '',
        sku: batch.sku || '',
        expiry_date: batch.expiry_date ? batch.expiry_date.split('T')[0] : '',
        manufacturing_date: batch.manufacturing_date ? batch.manufacturing_date.split('T')[0] : '',
        receipt_date: batch.last_receipt_date ? batch.last_receipt_date.split('T')[0] : '',
        bin_location: '',
        gtin: batch.gtin || '',
        asin: batch.asin || '',
        notes: '',
        unit_cost: '',
        received_quantity: ''
      });
    } else {
      setEditForm({
        batch_number: item.batch_number || '',
        sku: item.sku || '',
        expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
        manufacturing_date: item.manufacturing_date ? item.manufacturing_date.split('T')[0] : '',
        receipt_date: item.receipt_date ? item.receipt_date.split('T')[0] : '',
        bin_location: item.bin_location || '',
        gtin: item.gtin || '',
        asin: item.asin || '',
        notes: item.notes || '',
        unit_cost: item.unit_cost || '',
        received_quantity: item.received_quantity || item.quantity || ''
      });
    }
    setError(null); setEditDialogOpen(true);
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      if (editMode === 'bulk') {
        await updateBatchBulk(editItem.batch_number, editItem.sku, {
          new_batch_number: editForm.batch_number !== editItem.batch_number ? editForm.batch_number : undefined,
          expiry_date: editForm.expiry_date || null,
          manufacturing_date: editForm.manufacturing_date || null,
          receipt_date: editForm.receipt_date || null
        });
        setSuccess('Batch updated successfully');
      } else {
        await updateBatchItem(editItem.id, {
          batch_number: editForm.batch_number,
          sku: editForm.sku,
          expiry_date: editForm.expiry_date || null,
          manufacturing_date: editForm.manufacturing_date || null,
          receipt_date: editForm.receipt_date || null,
          bin_location: editForm.bin_location,
          gtin: editForm.gtin,
          asin: editForm.asin,
          notes: editForm.notes,
          unit_cost: editForm.unit_cost ? parseFloat(editForm.unit_cost) : undefined,
          received_quantity: editForm.received_quantity ? parseFloat(editForm.received_quantity) : undefined
        });
        setSuccess('Batch item updated successfully');
      }

      setEditDialogOpen(false);
      loadBatches();
      if (selectedBatch) {
        loadBatchDetails(selectedBatch);
      }
    } catch (err) {
      console.error('Error updating batch:', err);
      setError(err.message || 'Failed to update batch');
    } finally {
      setEditLoading(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (item, mode = 'single') => {
    setDeleteMode(mode);
    setDeleteItem(item);
    setError(null); setDeleteDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteMode === 'bulk') {
        await deleteBatchBulk(deleteItem.batch_number, deleteItem.sku);
        setSuccess(`Batch "${deleteItem.batch_number}" deleted successfully`);
      } else {
        await deleteBatchItem(deleteItem.id);
        setSuccess('Batch item deleted successfully');
      }

      setDeleteDialogOpen(false);
      loadBatches();
      if (selectedBatch && deleteMode === 'bulk') {
        setSelectedBatch(null);
      } else if (selectedBatch) {
        loadBatchDetails(selectedBatch);
      }
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError(err.message || 'Failed to delete batch');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle export
  const handleExport = () => {
    exportBatchesCSV(selectedBrand?.id);
    setSuccess('Export started - check your downloads');
  };

  // Handle import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImportLoading(true);
    setImportResult(null);

    try {
      const result = await importBatchesCSV(selectedFile, selectedBrand?.id);
      setImportResult(result);
      if (result.success) {
        loadBatches();
      }
    } catch (err) {
      console.error('Error importing batches:', err);
      setImportResult({
        success: false,
        message: err.message || 'Failed to import batches',
        results: { errors: [{ line: 0, error: err.message }] }
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Check if batch is expiring soon (within 90 days)
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 90 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    return expiry < new Date();
  };

  // Render batch list view
  const renderBatchList = () => (
    <>
      {/* Header */}
      <PageHeader
        primary="Batch Management"
        metadata={[{ label: 'View, edit, and manage inventory batches. Export data for analysis or import updates via CSV.' }]}
        actions={
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Brand</InputLabel>
            <Select
              value={selectedBrand?.id || ''}
              onChange={handleBrandChange}
              label="Select Brand"
            >
              {excludeCompanyLevel(brands).map(brand => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {/* Action Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <TextField
            placeholder="Search by batch number, SKU, product name, GTIN, or ASIN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
            size="small"
            sx={{ minWidth: 350 }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<TemplateIcon />}
              onClick={downloadBatchCSVTemplate}
              size="small"
            >
              Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setImportDialogOpen(true)}
              size="small"
            >
              Import CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              size="small"
              color="primary"
            >
              Export CSV
            </Button>
            <Tooltip title="Refresh data">
              <IconButton onClick={loadBatches} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="text.secondary" variant="caption" textTransform="uppercase" fontWeight={600}>
                Total Batches
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                {formatNumber(batches.length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography color="text.secondary" variant="caption" textTransform="uppercase" fontWeight={600}>
                Unique SKUs
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                {formatNumber(new Set(batches.map(b => b.sku)).size)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="text.secondary" variant="caption" textTransform="uppercase" fontWeight={600}>
                Expiring Soon
              </Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 1 }}>
                {formatNumber(batches.filter(b => isExpiringSoon(b.expiry_date)).length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'error.main' }}>
            <CardContent>
              <Typography color="text.secondary" variant="caption" textTransform="uppercase" fontWeight={600}>
                Expired
              </Typography>
              <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 1 }}>
                {formatNumber(batches.filter(b => isExpired(b.expiry_date)).length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Batch Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell width={50}></TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Batch Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Qty Received</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Cost</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warehouses</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Receipt</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBatches.map((batch, idx) => (
                <React.Fragment key={`${batch.batch_number}-${batch.sku}-${idx}`}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isExpired(batch.expiry_date)
                        ? 'rgba(244, 67, 54, 0.08)'
                        : isExpiringSoon(batch.expiry_date)
                        ? 'rgba(255, 152, 0, 0.08)'
                        : 'inherit',
                      '&:hover': {
                        backgroundColor: isExpired(batch.expiry_date)
                          ? 'rgba(244, 67, 54, 0.12)'
                          : isExpiringSoon(batch.expiry_date)
                          ? 'rgba(255, 152, 0, 0.12)'
                          : 'action.hover'
                      }
                    }}
                    onClick={() => setExpandedBatch(expandedBatch === idx ? null : idx)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedBatch === idx ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                        {batch.batch_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={batch.sku} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {batch.product_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {formatNumber(batch.total_received)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{batch.avg_unit_cost > 0 ? formatCurrency(batch.avg_unit_cost) : '—'}</TableCell>
                    <TableCell>
                      {batch.expiry_date ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isExpired(batch.expiry_date) && <ErrorIcon fontSize="small" color="error" />}
                          {isExpiringSoon(batch.expiry_date) && <WarningIcon fontSize="small" color="warning" />}
                          <Typography
                            variant="body2"
                            color={isExpired(batch.expiry_date) ? 'error' : isExpiringSoon(batch.expiry_date) ? 'warning.dark' : 'text.primary'}
                          >
                            {formatDate(batch.expiry_date)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(batch.warehouses || []).slice(0, 2).map(wh => (
                          <Chip key={wh} label={wh} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        ))}
                        {batch.warehouse_count > 2 && (
                          <Chip label={`+${batch.warehouse_count - 2}`} size="small" color="primary" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(batch.last_receipt_date)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadBatchDetails(batch);
                            }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Batch">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(batch, 'bulk', batch);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Batch">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteDialog(batch, 'bulk');
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, borderBottom: expandedBatch === idx ? 1 : 0 }}>
                      <Collapse in={expandedBatch === idx} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                            Receipt Details
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Warehouse</TableCell>
                                <TableCell>GRN #</TableCell>
                                <TableCell>Receipt Date</TableCell>
                                <TableCell>Expiry Date</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell align="right">Unit Cost</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(batch.receipt_details || []).map((detail, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>
                                    <Chip
                                      icon={<StoreIcon />}
                                      label={detail.warehouse_code}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>{detail.grn_number}</TableCell>
                                  <TableCell>{formatDate(detail.receipt_date)}</TableCell>
                                  <TableCell>
                                    {batch.expiry_date ? formatDate(batch.expiry_date) : '-'}
                                  </TableCell>
                                  <TableCell align="right">{formatNumber(detail.quantity)}</TableCell>
                                  <TableCell align="right">{formatCurrency(detail.unit_cost)}</TableCell>
                                  <TableCell align="center">
                                    <Stack direction="row" spacing={0} justifyContent="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => openEditDialog({
                                          id: detail.id,
                                          batch_number: batch.batch_number,
                                          sku: batch.sku,
                                          gtin: batch.gtin,
                                          asin: batch.asin,
                                          expiry_date: batch.expiry_date,
                                          receipt_date: detail.receipt_date,
                                          unit_cost: detail.unit_cost,
                                          received_quantity: detail.quantity
                                        }, 'single')}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => openDeleteDialog({ id: detail.id, ...detail, batch_number: batch.batch_number, sku: batch.sku }, 'single')}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {paginatedBatches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {loading ? 'Loading batches...' : 'No batches found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBatches.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </>
  );

  // Render batch detail view
  const renderBatchDetail = () => (
    <>
      {/* Header with back button */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => setSelectedBatch(null)} sx={{ backgroundColor: 'action.hover' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {selectedBatch?.batch_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              SKU: {selectedBatch?.sku} | {selectedBatch?.product_name || 'No product name'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              onClick={() => openEditDialog(selectedBatch, 'bulk', selectedBatch)}
            >
              Edit Batch
            </Button>
            <Button
              startIcon={<DeleteIcon />}
              variant="outlined"
              color="error"
              onClick={() => openDeleteDialog(selectedBatch, 'bulk')}
            >
              Delete Batch
            </Button>
          </Stack>
        </Box>
      </Paper>

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : batchDetails ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="caption" textTransform="uppercase">Total Received</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                    {formatNumber(batchDetails.summary?.total_received)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="caption" textTransform="uppercase">Avg Unit Cost</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                    {batchDetails.summary?.avg_unit_cost > 0 ? formatCurrency(batchDetails.summary.avg_unit_cost) : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="caption" textTransform="uppercase">Warehouses</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                    {batchDetails.summary?.warehouse_count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="caption" textTransform="uppercase">Expiry Date</Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                    {batchDetails.summary?.expiry_date ? formatDate(batchDetails.summary.expiry_date) : 'Not set'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="caption" textTransform="uppercase">Identifiers</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    GTIN: {batchDetails.summary?.gtin || '-'}
                  </Typography>
                  <Typography variant="body2">
                    ASIN: {batchDetails.summary?.asin || '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper sx={{ mb: 2 }}>
            <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Warehouse Breakdown" />
              <Tab label="All Receipts" />
              <Tab label="Transfer History" />
              <Tab label="Movement Timeline" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          {detailTab === 0 && (
            <Grid container spacing={2}>
              {(batchDetails.warehouse_breakdown || []).map((wh) => (
                <Grid item xs={12} md={6} key={wh.warehouse_code}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <StoreIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>{wh.warehouse_code}</Typography>
                        <Chip
                          label={`${formatNumber(wh.total_quantity)} units`}
                          color="primary"
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <List dense disablePadding>
                        {(wh.receipts || []).map((receipt, idx) => (
                          <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={receipt.grn_number}
                              secondary={`${formatDate(receipt.receipt_date)} - ${formatNumber(receipt.quantity)} units @ ${formatCurrency(receipt.unit_cost)}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                onClick={() => openEditDialog({
                                  id: receipt.id,
                                  batch_number: selectedBatch.batch_number,
                                  sku: selectedBatch.sku,
                                  bin_location: receipt.bin_location,
                                  notes: receipt.notes,
                                  expiry_date: batchDetails.summary?.expiry_date,
                                  gtin: batchDetails.summary?.gtin,
                                  asin: batchDetails.summary?.asin,
                                  receipt_date: receipt.receipt_date,
                                  unit_cost: receipt.unit_cost,
                                  received_quantity: receipt.quantity
                                }, 'single')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteDialog({
                                  id: receipt.id,
                                  grn_number: receipt.grn_number,
                                  batch_number: selectedBatch.batch_number,
                                  sku: selectedBatch.sku,
                                  quantity: receipt.quantity
                                }, 'single')}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {detailTab === 1 && (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>GRN #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Warehouse</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Receipt Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Received</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Accepted</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Rejected</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Cost</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Bin Location</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(batchDetails.all_receipts || []).map((receipt) => (
                      <TableRow key={receipt.id} hover>
                        <TableCell>{receipt.grn_number}</TableCell>
                        <TableCell>
                          <Chip label={receipt.warehouse_code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                        <TableCell align="right">{formatNumber(receipt.quantity)}</TableCell>
                        <TableCell align="right">{formatNumber(receipt.accepted_quantity)}</TableCell>
                        <TableCell align="right">{formatNumber(receipt.rejected_quantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(receipt.unit_cost)}</TableCell>
                        <TableCell>{receipt.bin_location || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {receipt.notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0} justifyContent="center">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog({
                                id: receipt.id,
                                batch_number: selectedBatch.batch_number,
                                sku: selectedBatch.sku,
                                bin_location: receipt.bin_location,
                                notes: receipt.notes,
                                expiry_date: batchDetails.summary?.expiry_date,
                                manufacturing_date: batchDetails.summary?.manufacturing_date,
                                gtin: batchDetails.summary?.gtin,
                                asin: batchDetails.summary?.asin,
                                receipt_date: receipt.receipt_date,
                                unit_cost: receipt.unit_cost,
                                received_quantity: receipt.quantity
                              }, 'single')}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog({
                                id: receipt.id,
                                grn_number: receipt.grn_number,
                                batch_number: selectedBatch.batch_number,
                                sku: selectedBatch.sku,
                                quantity: receipt.quantity
                              }, 'single')}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {detailTab === 2 && (
            <Paper>
              <CardContent>
                {(batchDetails.transfers || []).length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Transfer #</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Requested</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Shipped</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Received</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tracking</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batchDetails.transfers.map((transfer) => (
                          <TableRow key={transfer.id} hover>
                            <TableCell>{transfer.transfer_number}</TableCell>
                            <TableCell>
                              <Chip label={transfer.from_warehouse} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Chip label={transfer.to_warehouse} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>{formatDate(transfer.transfer_date)}</TableCell>
                            <TableCell align="right">{formatNumber(transfer.quantity_requested)}</TableCell>
                            <TableCell align="right">{formatNumber(transfer.quantity_shipped)}</TableCell>
                            <TableCell align="right">{formatNumber(transfer.quantity_received)}</TableCell>
                            <TableCell>
                              <Chip
                                label={transfer.status}
                                size="small"
                                color={transfer.status === 'completed' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{transfer.tracking_number || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No transfers found for this batch
                  </Typography>
                )}
              </CardContent>
            </Paper>
          )}

          {detailTab === 3 && (
            <Paper>
              <CardContent>
                {batchHistory?.events?.length > 0 ? (
                  <List>
                    {batchHistory.events.map((event, idx) => (
                      <ListItem key={idx} divider={idx < batchHistory.events.length - 1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          {event.event_type === 'receipt' && <CheckIcon color="success" />}
                          {event.event_type === 'transfer_out' && <ShippingIcon color="warning" />}
                          {event.event_type === 'transfer_in' && <InventoryIcon color="primary" />}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>{event.description}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(event.event_date)} | Ref: {event.reference_number}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${event.quantity > 0 ? '+' : ''}${formatNumber(event.quantity)}`}
                            color={event.quantity > 0 ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No movement history found
                  </Typography>
                )}
              </CardContent>
            </Paper>
          )}
        </>
      ) : null}
    </>
  );

  // Main render
  if (!selectedBrand) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" fontWeight={700}>
              Batch Management
            </Typography>
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel>Select Brand</InputLabel>
              <Select
                value=""
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {excludeCompanyLevel(brands).map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
        <Alert severity="info">Please select a brand to view batch management</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" variant="filled">
          {success}
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !batches.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : selectedBatch ? (
        renderBatchDetail()
      ) : (
        renderBatchList()
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editMode === 'bulk' ? 'Edit Batch' : 'Edit Batch Item'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Batch Number"
              value={editForm.batch_number}
              onChange={(e) => setEditForm({ ...editForm, batch_number: e.target.value })}
              fullWidth
              size="small"
            />
            {editMode === 'single' && (
              <>
                <TextField
                  label="SKU"
                  value={editForm.sku}
                  onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                  fullWidth
                  size="small"
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={editForm.received_quantity}
                      onChange={(e) => setEditForm({ ...editForm, received_quantity: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Unit Cost"
                      type="number"
                      value={editForm.unit_cost}
                      onChange={(e) => setEditForm({ ...editForm, unit_cost: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <TextField
              label="Receipt Date"
              type="date"
              value={editForm.receipt_date}
              onChange={(e) => setEditForm({ ...editForm, receipt_date: e.target.value })}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '2000-01-01' }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Expiry Date"
                  type="date"
                  value={editForm.expiry_date}
                  onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '2000-01-01' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Manufacturing Date"
                  type="date"
                  value={editForm.manufacturing_date}
                  onChange={(e) => setEditForm({ ...editForm, manufacturing_date: e.target.value })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '2000-01-01' }}
                />
              </Grid>
            </Grid>
            {editMode === 'single' && (
              <>
                <TextField
                  label="Bin Location"
                  value={editForm.bin_location}
                  onChange={(e) => setEditForm({ ...editForm, bin_location: e.target.value })}
                  fullWidth
                  size="small"
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="GTIN"
                      value={editForm.gtin}
                      onChange={(e) => setEditForm({ ...editForm, gtin: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="ASIN"
                      value={editForm.asin}
                      onChange={(e) => setEditForm({ ...editForm, asin: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <DialogContentText>
            {deleteMode === 'bulk' ? (
              <>
                Are you sure you want to delete the entire batch <strong>"{deleteItem?.batch_number}"</strong> for SKU <strong>{deleteItem?.sku}</strong>?
                <br /><br />
                This will delete all receipt items associated with this batch. This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete this batch item?
                <br /><br />
                <strong>GRN:</strong> {deleteItem?.grn_number}<br />
                <strong>Quantity:</strong> {formatNumber(deleteItem?.quantity)} units
                <br /><br />
                This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => !importLoading && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Import Batches from CSV
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>How to use CSV Import:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Export existing batches to get the correct format</li>
                <li>Edit the CSV file with your changes</li>
                <li>Keep the <code>item_id</code> column to update existing items</li>
                <li>Leave <code>item_id</code> empty to create new entries</li>
              </Typography>
            </Alert>

            <Box
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: selectedFile ? 'primary.50' : 'grey.50',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', backgroundColor: 'primary.50' }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <>
                  <CheckIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>{selectedFile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB - Click to change
                  </Typography>
                </>
              ) : (
                <>
                  <ImportIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>Click to select CSV file</Typography>
                  <Typography variant="body2" color="text.secondary">or drag and drop</Typography>
                </>
              )}
            </Box>

            {importLoading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  Processing CSV file...
                </Typography>
              </Box>
            )}

            {importResult && (
              <Alert
                severity={importResult.success ? 'success' : 'error'}
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" fontWeight={500}>{importResult.message}</Typography>
                {importResult.results && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Updated: {importResult.results.updated} | Created: {importResult.results.created} | Errors: {importResult.results.errors?.length || 0}
                    </Typography>
                    {importResult.results.errors?.length > 0 && (
                      <Box sx={{ mt: 1, maxHeight: 150, overflow: 'auto' }}>
                        {importResult.results.errors.slice(0, 10).map((err, i) => (
                          <Typography key={i} variant="caption" display="block" color="error">
                            Line {err.line}: {err.error}
                          </Typography>
                        ))}
                        {importResult.results.errors.length > 10 && (
                          <Typography variant="caption" color="text.secondary">
                            ...and {importResult.results.errors.length - 10} more errors
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            startIcon={<TemplateIcon />}
            onClick={downloadBatchCSVTemplate}
            disabled={importLoading}
          >
            Download Template
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setImportDialogOpen(false)} disabled={importLoading}>
            {importResult?.success ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!selectedFile || importLoading}
          >
            {importLoading ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchManagement;
